import React from 'react';
import { NoRecordFound } from '../../../../components/Common/NoRecordFound';
import { AdminTableHeader } from '../../../../components/Admin';
import { ProjectPreOrder, ProjectPreOrdersSummary } from '../services/Classrooms.service';

interface AdminPreOrdersTabProps {
  preOrders: ProjectPreOrder[];
  summary: ProjectPreOrdersSummary;
}

type Tone = 'green' | 'amber' | 'red' | 'gray';
const toneClass: Record<Tone, string> = {
  green: 'bg-green-100 text-green-800',
  amber: 'bg-amber-100 text-amber-800',
  red: 'bg-red-100 text-red-800',
  gray: 'bg-gray-100 text-gray-600',
};
const Pill: React.FC<{ tone: Tone; title?: string; children: React.ReactNode }> = ({ tone, title, children }) => (
  <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${toneClass[tone]}`} title={title}>
    {children}
  </span>
);

// A 'no' flyer decision means the parent opted out ("Declined").
const paymentBadge = (p: ProjectPreOrder): { tone: Tone; label: string; title: string } => {
  if (p.orderDecision === 'no') {
    return { tone: 'gray', label: 'Declined', title: 'Parent declined to order on the flyer' };
  }
  switch (p.status) {
    case 'confirmed':
      return { tone: 'green', label: 'Paid', title: "Parent's payment was confirmed (Stripe webhook fired)" };
    case 'cancelled':
      return { tone: 'red', label: 'Cancelled', title: 'Payment was cancelled or failed' };
    default:
      return { tone: 'amber', label: 'Not confirmed', title: 'Flyer intent only — no payment recorded yet' };
  }
};

const columns = [
  { key: 'student', label: 'Student' },
  { key: 'decision', label: 'Flyer decision' },
  { key: 'payment', label: 'Payment' },
  { key: 'awaiting', label: 'Awaiting shipment' },
  { key: 'stripe', label: 'Stripe ref' },
];

const AdminPreOrdersTab: React.FC<AdminPreOrdersTabProps> = ({ preOrders, summary }) => {
  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-2 mb-3 text-xs">
        <Pill tone="gray">Total {summary.total}</Pill>
        <Pill tone="green">Paid {summary.confirmed}</Pill>
        <Pill tone="amber">Not confirmed {summary.pending}</Pill>
        <Pill tone="red">Cancelled {summary.cancelled}</Pill>
      </div>

      <div className="w-full overflow-x-auto">
        <table className="min-w-full border border-gray-300">
          <AdminTableHeader columns={columns} />
          <tbody>
            {preOrders && preOrders.length > 0 ? (
              preOrders.map((p, index) => {
                const badge = paymentBadge(p);
                const name = [p.firstName, p.lastName].filter(Boolean).join(' ') || p.studentUsername || '—';
                return (
                  <tr key={p.id} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                    <td className="py-2 text-left px-4 border-b text-sm">
                      <div className="font-medium">{name}</div>
                      {p.studentUsername && <div className="text-xs text-gray-500">{p.studentUsername}</div>}
                    </td>
                    <td className="py-2 text-left px-4 border-b text-sm capitalize">
                      {p.orderDecision === 'yes' ? 'Yes' : p.orderDecision === 'no' ? 'No' : '—'}
                    </td>
                    <td className="py-2 text-left px-4 border-b">
                      <Pill tone={badge.tone} title={badge.title}>{badge.label}</Pill>
                    </td>
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {p.status === 'confirmed' ? (
                        p.preOrderBookCount > 0 ? (
                          <span className="text-amber-700 font-medium" title="Paid and not yet shipped">
                            {p.preOrderBookCount} awaiting
                          </span>
                        ) : (
                          <span className="text-gray-500" title="Paid entitlement already shipped">Shipped</span>
                        )
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-2 text-left px-4 border-b text-xs font-mono text-gray-600 max-w-[220px] truncate">
                      {/* Refunds are manual in the Stripe dashboard — deep-link the payment when
                          we have its id; older pre-orders only carry the session id as text. */}
                      {p.stripePaymentIntentId || p.stripeInvoiceId ? (
                        <a
                          href={
                            p.stripePaymentIntentId
                              ? `https://dashboard.stripe.com/payments/${encodeURIComponent(p.stripePaymentIntentId)}`
                              : `https://dashboard.stripe.com/invoices/${encodeURIComponent(p.stripeInvoiceId!)}`
                          }
                          target="_blank"
                          rel="noreferrer"
                          className="text-storyblue underline"
                          title="Open this payment in the Stripe dashboard (refunds are manual there)"
                        >
                          {p.stripePaymentIntentId || p.stripeInvoiceId} ↗
                        </a>
                      ) : (
                        <span title={p.sessionId || ''}>{p.sessionId || <span className="text-gray-400">—</span>}</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <NoRecordFound colSpan={columns.length} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPreOrdersTab;
